import { useState, useRef } from 'react';
import api from '../../api/axios';

const REQUIRED = ['name', 'phone'];
const ALL_FIELDS = ['name', 'phone', 'source', 'product_tag', 'status', 'notes'];
const SAMPLE_CSV = `name,phone,source,product_tag,status,notes
Arun Kumar,919876543210,manual,Cotton Saree,new,Interested in bulk order
Priya S,918765432109,whatsapp,Kanjivaram,hot,Called twice
`;

const bizData = JSON.parse(localStorage.getItem('vflow_business') || '{}');
const userData = JSON.parse(localStorage.getItem('vflow_user') || '{}');
const bizId = bizData?.id || userData?.business_id || userData?.businesses?.[0]?.id;

export function LeadCSVImport({ onClose, onDone }) {
  const [step, setStep] = useState('upload');
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [progress, setProgress] = useState({ done: 0, total: 0, errors: [] });
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    const hdrs = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    const data = lines.slice(1).map(line => {
      const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      const obj = {};
      hdrs.forEach((h, i) => obj[h] = vals[i] || '');
      return obj;
    }).filter(r => Object.values(r).some(v => v));
    return { hdrs, data };
  };

  const onFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const { hdrs, data } = parseCSV(ev.target.result);
      setHeaders(hdrs);
      setRows(data);
      const autoMap = {};
      ALL_FIELDS.forEach(f => {
        const match = hdrs.find(h => h.toLowerCase().replace(/\s/g,'_') === f);
        if (match) autoMap[f] = match;
      });
      setMapping(autoMap);
      setStep('map');
    };
    reader.readAsText(file);
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'sample_leads.csv';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const startImport = async () => {
    setImporting(true);
    setStep('importing');
    const errors = [];
    let done = 0;
    setProgress({ done: 0, total: rows.length, errors: [] });
    for (const row of rows) {
      const lead = { business_id: bizId };
      ALL_FIELDS.forEach(f => { if (mapping[f]) lead[f] = row[mapping[f]] || ''; });
      if (!lead.phone) { errors.push(`Row skipped: no phone`); done++; setProgress({ done, total: rows.length, errors: [...errors] }); continue; }
      lead.source = lead.source || 'manual';
      lead.status = lead.status || 'new';
      try {
        await api.post('/leads', lead);
      } catch (e) {
        errors.push(`${lead.phone}: ${e.message}`);
      }
      done++;
      setProgress({ done, total: rows.length, errors: [...errors] });
    }
    setImporting(false);
    setStep('done');
  };

  const inp = { width: '100%', padding: '8px 12px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 24, width: 520, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a2e' }}>📥 CSV Import</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#aaa' }}>✕</button>
        </div>

        {step === 'upload' && (
          <div>
            <div style={{ border: '2px dashed #e0e0e0', borderRadius: 12, padding: 32, textAlign: 'center', marginBottom: 16, cursor: 'pointer', background: '#fafafa' }} onClick={() => fileRef.current.click()}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#333' }}>Click to upload CSV file</div>
              <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>Columns: name, phone, source, product_tag, status, notes</div>
              <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={onFile} />
            </div>
            <button onClick={downloadSample} style={{ width: '100%', padding: '10px 0', background: '#f0f0f0', color: '#333', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              ⬇ Download Sample CSV
            </button>
          </div>
        )}

        {step === 'map' && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 12 }}>
              {rows.length} rows found. Map your columns:
            </div>
            {ALL_FIELDS.map(f => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div style={{ width: 110, fontSize: 12, fontWeight: 700, color: REQUIRED.includes(f) ? '#c62828' : '#555' }}>
                  {f}{REQUIRED.includes(f) ? ' *' : ''}
                </div>
                <select style={{ ...inp, flex: 1 }} value={mapping[f] || ''} onChange={e => setMapping({ ...mapping, [f]: e.target.value })}>
                  <option value="">— skip —</option>
                  {headers.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            ))}
            <div style={{ background: '#f5f5f5', borderRadius: 8, padding: 12, marginTop: 8, marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 6 }}>Preview (first 3 rows):</div>
              {rows.slice(0,3).map((row, i) => (
                <div key={i} style={{ fontSize: 11, color: '#555', marginBottom: 2 }}>
                  {mapping.name ? row[mapping.name] : '?'} · {mapping.phone ? row[mapping.phone] : '?'} · {mapping.product_tag ? row[mapping.product_tag] : '—'}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setStep('upload')} style={{ flex: 1, padding: '10px 0', background: '#f0f0f0', color: '#333', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>← Back</button>
              <button onClick={startImport} disabled={!mapping.phone} style={{ flex: 2, padding: '10px 0', background: mapping.phone ? '#25d366' : '#ccc', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: mapping.phone ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
                Import {rows.length} Leads →
              </button>
            </div>
          </div>
        )}

        {step === 'importing' && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Importing... {progress.done}/{progress.total}</div>
            <div style={{ height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
              <div style={{ width: `${progress.total ? (progress.done/progress.total)*100 : 0}%`, height: '100%', background: '#25d366', borderRadius: 4, transition: 'width 0.3s' }} />
            </div>
            {progress.errors.length > 0 && (
              <div style={{ fontSize: 11, color: '#f44336', textAlign: 'left' }}>{progress.errors.slice(-3).join('\n')}</div>
            )}
          </div>
        )}

        {step === 'done' && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#1a1a2e', marginBottom: 8 }}>Import Complete!</div>
            <div style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>{progress.total - progress.errors.length} leads imported successfully</div>
            {progress.errors.length > 0 && <div style={{ fontSize: 12, color: '#f44336', marginBottom: 12 }}>{progress.errors.length} rows skipped</div>}
            <button onClick={() => { onDone(); onClose(); }} style={{ padding: '10px 32px', background: '#25d366', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              View Leads
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
